/**
 * Whose money you are looking at.
 *
 * This is a LENS, not a permission. Everyone in the household can see everything — roles
 * decide what you may change, not what you may read (splitting the data would make net
 * worth and spending-against-income meaningless). The scope simply narrows the view to one
 * person's attributed transactions and accounts when you want to answer "what did I spend"
 * rather than "what did we spend".
 *
 * It defaults to the whole household, because that is what the app is for. The choice is
 * remembered per browser.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

const STORAGE_KEY = 'pf.scope.memberId'

/** `null` means the whole household. */
export type Scope = string | null

interface ScopeContextValue {
  memberId: Scope
  setMemberId: (memberId: Scope) => void
  isHousehold: boolean
}

const ScopeContext = createContext<ScopeContextValue | null>(null)

export function ScopeProvider({ children }: { children: ReactNode }) {
  const [memberId, setStored] = useState<Scope>(() => window.localStorage.getItem(STORAGE_KEY) || null)

  const setMemberId = useCallback((next: Scope) => {
    setStored(next)
    if (next) window.localStorage.setItem(STORAGE_KEY, next)
    else window.localStorage.removeItem(STORAGE_KEY)
  }, [])

  const value = useMemo(
    () => ({ memberId, setMemberId, isHousehold: memberId === null }),
    [memberId, setMemberId],
  )

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
}

export function useScope(): ScopeContextValue {
  const context = useContext(ScopeContext)
  if (!context) throw new Error('useScope must be used inside <ScopeProvider>.')
  return context
}

/** Safe outside the shell (the login and admin screens render no scope). */
export function useOptionalScope(): ScopeContextValue | null {
  return useContext(ScopeContext)
}

/**
 * Spreads into a query's params. Undefined rather than null when unscoped, so the
 * parameter is omitted from the URL entirely instead of sending `memberId=`.
 */
export function scopeParam(memberId: Scope): { memberId?: string } {
  return memberId ? { memberId } : {}
}
